import { draft, league } from "../index.ts";
import { idb } from "../../db/index.ts";
import { g, helpers } from "../../util/index.ts";
import type { PhaseReturn } from "../../../common/types.ts";
import { buildLotteryPickByTeam } from "../season/reportCards.ts";
import * as challengeBackend from "../../util/challengeBackend/index.ts";
import getChallenge from "../league/getChallenge.ts";

// Executive Challenge end-of-contest hook. Entering AFTER_DRAFT of the
// challenge's end season (right after the endSeason draft) is the finish line:
// capture the locked result from the report card (computed at newPhaseDraft,
// after the lottery), flip the challenge to complete, and submit the entry to
// the leaderboard backend. Idempotent on status — a re-entered phase can't
// double-finalize, and "complete" is what gates the save read-only.
export const finalizeChallenge = async (): Promise<PhaseReturn | undefined> => {
	const ch = getChallenge();
	if (ch?.status !== "active" || g.get("season") !== ch.endSeason) {
		return;
	}

	const ts = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [
		ch.tid,
		ch.endSeason,
	]);
	const rc = ts?.reportCard;
	if (!ts || !rc) {
		// Shouldn't happen (newPhaseDraft just computed it), but never strand the
		// save in a half-finalized state — leave active and try again next pass.
		return;
	}

	const lotteryPickByTeam = await buildLotteryPickByTeam(ch.endSeason);

	const result = {
		gmScore: rc.gmScore,
		grade: rc.grade,
		draftPick: lotteryPickByTeam.get(ch.tid) ?? null,
		wins: ts.won,
		losses: ts.lost,
	};

	await league.setGameAttributes({
		challenge: { ...ch, status: "complete", result },
	});

	// Fail-open: the local result above is already locked in; a backend failure
	// only means no share link yet (the result screen can retry the submit).
	try {
		const { shareId, shareExpiresAt } = await challengeBackend.submitEntry({
			code: ch.code,
			playerId: ch.playerId,
			...result,
		});
		await league.setGameAttributes({
			challenge: {
				...ch,
				status: "complete",
				result: { ...result, shareId, shareExpiresAt },
			},
		});
	} catch (error) {
		console.error("Challenge entry submit failed", error);
	}

	return {
		updateEvents: ["playerMovement"],
		redirect: {
			url: helpers.leagueUrl(["challenge_result"]),
			text: "View your result",
		},
	};
};

const newPhaseAfterDraft = async (): Promise<PhaseReturn> => {
	// In case some weird situation results in games still in the schedule, clear them
	await idb.cache.schedule.clear();

	await draft.genPicks({
		afterDraft: true,
	});

	// Delete any old draft picks
	const draftPicks = await idb.cache.draftPicks.getAll();
	for (const dp of draftPicks) {
		if (typeof dp.season !== "number" || dp.season <= g.get("season")) {
			await idb.cache.draftPicks.delete(dp.dpid);
		}
	}

	// Already set in afterPicks, but do again just to be sure
	await league.setGameAttributes({
		numDraftPicksCurrent: undefined,
	});

	const challengeReturn = await finalizeChallenge();
	if (challengeReturn) {
		return challengeReturn;
	}

	return {
		updateEvents: ["playerMovement"],
	};
};

export default newPhaseAfterDraft;
